import React, {
  useEffect,
  useReducer,
  useCallback,
  useRef,
  useMemo,
  memo,
} from "react";
import ComponentCustomModal from "../../components/common/customModal/ComponentCustomModal";
import useISPowner from "../../hooks/useISPOwner";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import socket, { connectSocket } from "../../socket";
import { badge } from "../../components/common/Utils";

const MAX_CHUNK_RECORDS = 500;

const INITIAL_STATE = {
  syncing: false,
  panel: "",
  totalMikroTiks: 0,
  totalCustomers: 0,
  processed: 0,
  updated: 0,
  unchanged: 0,
  skipped: 0,
  failed: 0,
  routersById: {},
  routerOrder: [],
  chunkData: [],
};

const KNOWN_ACTIONS = new Set(["updated", "unchanged", "skipped", "failed"]);

function tallyRecord(counts, data) {
  const actionKey = String(data.action || "").toLowerCase();
  const isKnown = KNOWN_ACTIONS.has(actionKey);

  if (actionKey === "updated") counts.updated++;
  else if (actionKey === "unchanged") counts.unchanged++;
  else if (actionKey === "skipped") counts.skipped++;
  else if (actionKey === "failed") counts.failed++;

  if (isKnown) {
    counts.processed =
      counts.updated + counts.unchanged + counts.skipped + counts.failed;
  } else {
    counts.processed++;
  }
}

function syncReducer(state, action) {
  switch (action.type) {
    case "RESET": {
      return {
        ...INITIAL_STATE,
        syncing: state.syncing,
        panel: state.panel,
      };
    }

    case "SYNC_START": {
      const { data } = action;

      return {
        ...state,
        syncing: true,
        totalMikroTiks: data.totalMikroTiks ?? 0,
        totalCustomers: data.totalCustomers ?? 0,
      };
    }

    case "SYNC_USER": {
      const { data } = action;

      const nextChunk = [
        ...state.chunkData,
        { ...data, receivedAt: Date.now() },
      ];
      const trimmedChunk =
        nextChunk.length > MAX_CHUNK_RECORDS
          ? nextChunk.slice(-MAX_CHUNK_RECORDS)
          : nextChunk;

      const counts = {
        updated: state.updated,
        unchanged: state.unchanged,
        skipped: state.skipped,
        failed: state.failed,
        processed: state.processed,
      };
      tallyRecord(counts, data);

      return {
        ...state,
        ...counts,
        chunkData: trimmedChunk,
      };
    }

    case "SYNC_USER_BATCH": {
      const records = action.records;
      if (!records || !records.length) return state;

      const counts = {
        updated: state.updated,
        unchanged: state.unchanged,
        skipped: state.skipped,
        failed: state.failed,
        processed: state.processed,
      };

      const now = Date.now();
      const stamped = new Array(records.length);

      for (let i = 0; i < records.length; i++) {
        const data = records[i];
        tallyRecord(counts, data);
        stamped[i] = { ...data, receivedAt: now };
      }

      const nextChunk = state.chunkData.concat(stamped);
      const trimmedChunk =
        nextChunk.length > MAX_CHUNK_RECORDS
          ? nextChunk.slice(-MAX_CHUNK_RECORDS)
          : nextChunk;

      return {
        ...state,
        ...counts,
        chunkData: trimmedChunk,
      };
    }

    case "SYNC_ROUTER": {
      const { data } = action;
      const id = String(data.mikrotikId);

      const existing = state.routersById[id];

      const routerData = {
        ...existing,
        ...data,
        completed: true,
      };

      const routerOrder = existing
        ? state.routerOrder
        : [...state.routerOrder, id];

      return {
        ...state,
        routersById: {
          ...state.routersById,
          [id]: routerData,
        },
        routerOrder,
      };
    }

    case "SYNC_COMPLETE": {
      const { data } = action;

      let routersById = state.routersById;
      let routerOrder = state.routerOrder;

      if (Array.isArray(data.results)) {
        routersById = {};
        routerOrder = [];

        data.results.forEach((router) => {
          const id = String(router.mikrotikId ?? router.mikrotik);

          routersById[id] = { ...router, completed: true };
          routerOrder.push(id);
        });
      }

      return {
        ...state,
        syncing: false,
        totalMikroTiks: data.totalMikroTiks ?? 0,
        totalCustomers: data.totalCustomers ?? 0,
        processed: data.processed ?? state.processed,
        updated: data.updated ?? state.updated,
        unchanged: data.unchanged ?? state.unchanged,
        skipped: data.skipped ?? state.skipped,
        failed: data.failed ?? state.failed,
        routersById,
        routerOrder,
      };
    }

    case "SYNC_ERROR": {
      return {
        ...state,
        syncing: false,
      };
    }

    case "SET_PANEL": {
      return {
        ...state,
        panel: action.panel,
        syncing: true,
      };
    }

    default:
      return state;
  }
}

const STAT_STYLES = {
  updated: { icon: "\u2713", bg: "success" },
  unchanged: { icon: "\u2212", bg: "secondary" },
  skipped: { icon: "\u21B7", bg: "warning" },
  failed: { icon: "\u2715", bg: "danger" },
};

const CHUNK_VARIANT = {
  updated: "success",
  unchanged: "secondary",
  skipped: "warning",
  failed: "danger",
};

const ChunkRow = memo(function ChunkRow({ data }) {
  const actionKey = String(data.action || "").toLowerCase();
  const variant = CHUNK_VARIANT[actionKey] || "info";

  return (
    <div
      className={`smts-chunk-row px-3 py-2 border-start border-4 border-${variant}`}
      style={{ contentVisibility: "auto", containIntrinsicSize: "0 64px" }}
    >
      <div className="d-flex justify-content-between align-items-center gap-2">
        <strong className="text-truncate">{data.name || "Unknown user"}</strong>
        <span>{data.action ? badge(data.action.toUpperCase()) : "Info"}</span>
      </div>

      <div className="small text-muted">
        {data.profile && <>Profile: {data.profile}</>}

        {typeof data.disabled === "boolean" && (
          <>
            {" | "}
            {data.disabled ? "Disabled" : "Enabled"}
          </>
        )}
      </div>

      {data.message && <div className="small">{data.message}</div>}

      {(data.processed != null || data.total != null) && (
        <div className="small text-muted mt-1">
          Router progress: {data.processed ?? 0} / {data.total ?? 0}
        </div>
      )}
    </div>
  );
});

const SoftwareAndMikroTikSync = ({ show, setShow }) => {
  const { t } = useTranslation();
  const { ispOwnerId, currentUser } = useISPowner();

  const [state, dispatch] = useReducer(syncReducer, INITIAL_STATE);
  const syncingRef = useRef(false);
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    syncingRef.current = state.syncing;
  }, [state.syncing]);

  useEffect(() => {
    if (!show) {
      return;
    }

    const handleSyncStarted = (data) => {
      dispatch({ type: "SYNC_START", data });
    };

    const handleSyncUser = (data) => {
      dispatch({ type: "SYNC_USER", data });
    };

    const handleSyncUserBatch = (payload) => {
      const records = payload?.records;
      if (!records || !records.length) return;
      dispatch({ type: "SYNC_USER_BATCH", records });
    };

    const handleSyncRouter = (data) => {
      dispatch({ type: "SYNC_ROUTER", data });
    };

    const handleSyncComplete = (data) => {
      dispatch({ type: "SYNC_COMPLETE", data });

      toast.success(t("softwareAndMikroTikSyncCompleted"));
    };

    const handleSyncError = (data) => {
      dispatch({ type: "SYNC_ERROR" });

      toast.error(t("softwareAndMikroTikSyncFailed"));
    };

    socket.on("SoftwareAndMikroTikSyncStarted", handleSyncStarted);
    socket.on("SoftwareAndMikroTikSyncUser", handleSyncUser);
    socket.on("SoftwareAndMikroTikSyncUserBatch", handleSyncUserBatch);
    socket.on("SoftwareAndMikroTikSyncRouter", handleSyncRouter);
    socket.on("SoftwareAndMikroTikSyncComplete", handleSyncComplete);
    socket.on("SoftwareAndMikroTikSyncError", handleSyncError);

    return () => {
      socket.off("SoftwareAndMikroTikSyncStarted", handleSyncStarted);
      socket.off("SoftwareAndMikroTikSyncUser", handleSyncUser);
      socket.off("SoftwareAndMikroTikSyncUserBatch", handleSyncUserBatch);
      socket.off("SoftwareAndMikroTikSyncRouter", handleSyncRouter);
      socket.off("SoftwareAndMikroTikSyncComplete", handleSyncComplete);
      socket.off("SoftwareAndMikroTikSyncError", handleSyncError);
    };
  }, [show]);

  const handleSync = useCallback(
    (selectedPanel) => {
      if (!ispOwnerId) {
        toast.error("ISP owner information is missing");
        return;
      }

      if (!currentUser?.user?.id) {
        toast.error("User information is missing");
        return;
      }

      if (syncingRef.current) {
        return;
      }

      connectSocket(currentUser.user.id);

      dispatch({ type: "RESET" });
      dispatch({ type: "SET_PANEL", panel: selectedPanel });

      socket.emit("SoftwareAndMikroTikSync", {
        ispOwner: ispOwnerId,
        ...(selectedPanel === "reseller" && { reseller: true }),
      });
    },
    [ispOwnerId, currentUser],
  );

  const progressPercentage = useMemo(() => {
    if (!state.totalCustomers) {
      return 0;
    }

    return Math.min(
      100,
      Math.round((state.processed / state.totalCustomers) * 100),
    );
  }, [state.processed, state.totalCustomers]);

  const routerList = useMemo(
    () => state.routerOrder.map((id) => state.routersById[id]),
    [state.routerOrder, state.routersById],
  );

  const reversedChunkData = useMemo(
    () => state.chunkData.slice().reverse(),
    [state.chunkData],
  );

  const handleClose = useCallback(() => {
    if (syncingRef.current) {
      toast.info("Sync is running — please wait for it to finish.");
      return;
    }

    setShow(false);
  }, [setShow]);

  const progressBarVariant = state.failed > 0 ? "warning" : "primary";

  const SummaryStat = memo(function SummaryStat({ label, value, variant }) {
    const { icon, bg } = STAT_STYLES[variant] ?? { icon: "", bg: "secondary" };

    return (
      <div className="col-3">
        <div
          className="card border-0 shadow-sm h-100 smts-stat-card"
          style={{ transition: "transform 0.15s ease, box-shadow 0.15s ease" }}
        >
          <div className="card-body p-2 px-2 px-sm-3">
            <div className="d-flex align-items-center gap-2">
              <div
                className={`d-flex align-items-center justify-content-center rounded-circle bg-${bg} bg-opacity-10`}
                style={{ width: 30, height: 30, flexShrink: 0 }}
              >
                <span className={`text-${bg}`} style={{ fontSize: 15 }}>
                  {icon}
                </span>
              </div>

              <div
                className={`fw-bold text-${bg}`}
                style={{
                  fontSize: "18px",
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {value}
              </div>
            </div>

            <div
              className="text-muted mt-2"
              style={{
                fontSize: "11px",
                fontWeight: 500,
                lineHeight: 1.3,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {t(label)}
            </div>
          </div>
        </div>
      </div>
    );
  });

  const RouterCard = memo(function RouterCard({ router, index }) {
    const failedRouter = router.status === false || router.ok === false;
    const variant = failedRouter ? "danger" : "success";

    return (
      <div
        className={`smts-router-card card border-0 border-start border-4 border-${variant} shadow-sm mb-2`}
      >
        <div className="card-body py-2 px-3">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <span className="smts-router-index badge rounded-pill bg-light text-dark border">
                {index + 1}
              </span>
              <strong>{router.mikrotik}</strong>
            </div>
            <span>
              {badge((failedRouter ? "Failed" : "Completed").toUpperCase())}
            </span>
          </div>

          {router.host && (
            <small className="text-muted d-block mt-1">{router.host}</small>
          )}

          <div className="smts-router-stats d-flex flex-wrap gap-2 mt-2">
            <span className="badge bg-light text-dark border">
              {t("processed")}: {router.processed ?? 0}
            </span>
            <span className="badge bg-success-subtle text-success border border-success-subtle">
              {t("updated")}: {router.updated ?? 0}
            </span>
            <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle">
              {t("unchanged")}: {router.unchanged ?? 0}
            </span>
            <span className="badge bg-danger-subtle text-danger border border-danger-subtle">
              {t("failed")}: {router.failed ?? 0}
            </span>
          </div>
        </div>
      </div>
    );
  });

  return (
    <ComponentCustomModal
      show={show}
      setShow={handleClose}
      header={t("softwareAndMikroTikSync")}
    >
      <style>{`
        .smts-stat-value { font-size: 1.1rem; line-height: 1.1; }
        .smts-stat-label { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.03em; }
        .smts-stat-icon { font-size: 1rem; width: 1.5rem; text-align: center; }
        .smts-scroll { scrollbar-width: thin; }
        .smts-scroll::-webkit-scrollbar { width: 6px; }
        .smts-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.2); border-radius: 3px; }
        .smts-chunk-row:not(:last-child) { border-bottom: 1px solid rgba(0,0,0,0.06); }
        .smts-router-index { min-width: 1.75rem; }
      `}</style>

      <div className="displayGrid">
        <div>
          <div className="text-muted fw-bolder mb-0">
            {t("selectAdminorResellerToStartSynchronization")}
          </div>

          <div className="displayGrid2">
            <div className="message_radio">
              <input
                type="radio"
                id="admin"
                name="syncPanel"
                checked={state.panel === "admin"}
                value="admin"
                disabled={state.syncing}
                onChange={() => handleSync("admin")}
              />
              <label htmlFor="admin" className="fs-5 ms-2">
                {t("admin")}
              </label>
            </div>

            <div className="message_radio">
              <input
                type="radio"
                id="reseller"
                name="syncPanel"
                checked={state.panel === "reseller"}
                value="reseller"
                disabled={state.syncing}
                onChange={() => handleSync("reseller")}
              />
              <label htmlFor="reseller" className="fs-5 ms-2">
                {t("reseller")}
              </label>
            </div>
          </div>
        </div>

        <div>
          <div className="d-flex justify-content-between align-items-center">
            <p className="text-muted mb-1">
              {t("synchronizePPPoECustomersBetweenSoftwareAndMikroTik")}
            </p>
          </div>

          <div>
            <div
              className="progress"
              style={{ height: "10px" }}
              role="progressbar"
              aria-valuenow={progressPercentage}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className={`progress-bar bg-${progressBarVariant} ${
                  state.syncing
                    ? "progress-bar-striped progress-bar-animated"
                    : ""
                }`}
                style={{
                  width: `${progressPercentage}%`,
                  transition: "width 0.3s ease",
                }}
              />
            </div>

            <div className="d-flex justify-content-between mb-3">
              <div className="small text-muted mt-2">
                {state.processed} / {state.totalCustomers}{" "}
                {t("customersProcessed")}
              </div>
              <span className="fw-bold mt-2">{progressPercentage}%</span>
            </div>
          </div>
        </div>

        <div className="row g-2 mb-3">
          <SummaryStat
            label={t("updated")}
            value={state.updated}
            variant="updated"
          />
          <SummaryStat
            label={t("unchanged")}
            value={state.unchanged}
            variant="unchanged"
          />
          <SummaryStat
            label={t("skipped")}
            value={state.skipped}
            variant="skipped"
          />
          <SummaryStat
            label={t("failed")}
            value={state.failed}
            variant="failed"
          />
        </div>

        {routerList.length > 0 && (
          <div className="mb-3">
            <h6 className="mb-2">{t("mikroTikRouters")}</h6>

            <div
              className="smts-scroll"
              style={{ maxHeight: "220px", overflowY: "auto" }}
            >
              {routerList.map((router, index) => (
                <RouterCard
                  key={router.mikrotikId ?? `${router.mikrotik}-${index}`}
                  router={router}
                  index={index}
                />
              ))}
            </div>
          </div>
        )}

        {reversedChunkData.length > 0 && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <h6 className="mb-0">{t("liveSyncActivity")}</h6>
              <small className="text-muted">
                {reversedChunkData.length} {t("showingLatestRecords")}
              </small>
            </div>

            <div
              className="smts-scroll border rounded"
              style={{
                maxHeight: "300px",
                overflowY: "auto",
                contain: "content",
              }}
            >
              {reversedChunkData.map((data, index) => (
                <ChunkRow
                  key={`${data.customerId ?? data.name}-${data.receivedAt}-${index}`}
                  data={data}
                />
              ))}
            </div>
          </div>
        )}

        {state.syncing && (
          <div className="text-center mt-3">
            <span
              className="spinner-border spinner-border-sm text-primary me-2"
              role="status"
              aria-hidden="true"
            />
            <small className="text-muted">
              {t("leaseWaitSynchronizationIsRunning")}
            </small>
          </div>
        )}
      </div>
    </ComponentCustomModal>
  );
};

export default SoftwareAndMikroTikSync;
